import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Prepend backend URL for stored media paths (uploaded files return "/api/media/..").
export const mediaSrc = (src) => {
  if (!src) return src;
  return src.startsWith("http") ? src : `${BACKEND_URL}${src}`;
};

const TOKEN_KEY = "viimii_token";
const USER_KEY = "viimii_user";
const TOKEN_EXP_KEY = "viimii_token_exp";
const SESSION_DAYS = 14; // stay logged in for 2 weeks

const isExpired = () => {
  const exp = Number(localStorage.getItem(TOKEN_EXP_KEY) || 0);
  return !exp || Date.now() > exp;
};

export const getToken = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return null;
  if (isExpired()) {
    clearToken();
    return null;
  }
  return t;
};
export const setToken = (t) => {
  localStorage.setItem(TOKEN_KEY, t);
  localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + SESSION_DAYS * 24 * 60 * 60 * 1000));
};
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXP_KEY);
};
export const getUsername = () => localStorage.getItem(USER_KEY) || "Admin";

const authHeaders = () => ({ headers: { "X-Admin-Token": getToken() || "" } });

// ---- Auth ----
export async function login(username, password) {
  const { data } = await axios.post(`${API}/admin/login`, { username, password });
  setToken(data.token);
  if (data.username) localStorage.setItem(USER_KEY, data.username);
  return data;
}

// ---- Bookings ----
export async function createBooking(form) {
  const { data } = await axios.post(`${API}/bookings`, {
    name: form.name,
    phone: form.phone,
    service: form.service || "General",
    timing: form.timing || "Not Sure",
    message: form.message || "",
    amount: form.amount || 0,
    date: form.date || "",
    time: form.time || "",
    duration: form.duration || 60,
  });
  return data;
}
export async function listBookings() {
  const { data } = await axios.get(`${API}/bookings`, authHeaders());
  return data;
}
export async function patchBooking(id, patch) {
  const { data } = await axios.patch(`${API}/bookings/${id}`, patch, authHeaders());
  return data;
}
export async function removeBooking(id) {
  await axios.delete(`${API}/bookings/${id}`, authHeaders());
}

// ---- Gallery ----
export async function listGallery() {
  const { data } = await axios.get(`${API}/gallery`);
  return data;
}
export async function addGalleryUrl(src) {
  const fd = new FormData();
  fd.append("src", src);
  const { data } = await axios.post(`${API}/gallery`, fd, authHeaders());
  return data;
}
export async function addGalleryFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/gallery`, fd, authHeaders());
  return data;
}
export async function removeGallery(id) {
  await axios.delete(`${API}/gallery/${id}`, authHeaders());
}

// ---- Reels ----
export async function listReels() {
  const { data } = await axios.get(`${API}/reels`);
  return data;
}

export async function getInstagramFeed(limit = 12) {
  const { data } = await axios.get(`${API}/instagram/feed`, { params: { limit } });
  return data;
}

export async function getInstaManage() {
  const { data } = await axios.get(`${API}/instagram/manage`, authHeaders());
  return data;
}

export async function setHiddenReels(hidden) {
  const { data } = await axios.put(`${API}/instagram/hidden`, { hidden }, authHeaders());
  return data;
}
export async function addReelUrl(src, caption) {
  const fd = new FormData();
  fd.append("src", src);
  fd.append("caption", caption || "");
  const { data } = await axios.post(`${API}/reels`, fd, authHeaders());
  return data;
}
export async function addReelFile(file, caption) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("caption", caption || "");
  const { data } = await axios.post(`${API}/reels`, fd, authHeaders());
  return data;
}
export async function removeReel(id) {
  await axios.delete(`${API}/reels/${id}`, authHeaders());
}

// ---- Settings ----
export async function getSettings() {
  const { data } = await axios.get(`${API}/settings`);
  return data;
}
export async function saveSettings(s) {
  const { data } = await axios.put(`${API}/settings`, s, authHeaders());
  return data;
}

// ---- Staff & Salary ----
export async function listStaff() {
  const { data } = await axios.get(`${API}/staff`, authHeaders());
  return data;
}
export async function createStaff(s) {
  const { data } = await axios.post(`${API}/staff`, s, authHeaders());
  return data;
}
export async function updateStaff(id, s) {
  const { data } = await axios.put(`${API}/staff/${id}`, s, authHeaders());
  return data;
}
export async function deleteStaff(id) {
  await axios.delete(`${API}/staff/${id}`, authHeaders());
}
export async function addAdvance(id, body) {
  const { data } = await axios.post(`${API}/staff/${id}/advance`, body, authHeaders());
  return data;
}
export async function markAttendance(id, date, present) {
  const { data } = await axios.post(`${API}/staff/${id}/attendance`, { date, present }, authHeaders());
  return data;
}
export async function getSlip(id, month) {
  const { data } = await axios.get(`${API}/staff/${id}/slip`, { params: { month }, ...authHeaders() });
  return data;
}

// ---- Slots ----
export async function getSlots(service, date, duration) {
  const params = { service, date };
  if (duration) params.duration = duration;
  const { data } = await axios.get(`${API}/slots`, { params });
  return data;
}

// ---- Spin & Win ----
export async function addSpin(name, phone, prize) {
  const { data } = await axios.post(`${API}/spins`, { name, phone, prize });
  return data;
}
export async function listSpins() {
  const { data } = await axios.get(`${API}/spins`, authHeaders());
  return data;
}
export async function removeSpin(id) {
  await axios.delete(`${API}/spins/${id}`, authHeaders());
}

// ---- Track Bookings (public) ----
export async function trackBookings(phone) {
  const { data } = await axios.get(`${API}/bookings/track`, { params: { phone } });
  return data;
}

// ---- Web Push ----
export async function getVapidKey() {
  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  return data.publicKey;
}
export async function savePushSubscription(sub) {
  const { data } = await axios.post(`${API}/push/subscribe`, sub, authHeaders());
  return data;
}
export async function sendTestPush() {
  const { data } = await axios.post(`${API}/push/test`, {}, authHeaders());
  return data;
}
